// Onglet « À facturer » de la Facturation (transport seulement, lot T6, étape B1).
// Liste du serveur (GET /billing/to-invoice) : un bloc par payeur (donneur d'ordre, ou passager qui paie, donneur d'ordre
// rappelé), courses les plus anciennes en haut, total, « + de 30 jours » en rouge. « Facturer cette course »
// (POST /billing/invoice-ride) ; « Facturer le mois » : mois (précédent par défaut), courses cochées, total, « Créer la
// facture » (POST /billing/invoice-month). Une seule demande à la fois (boutons grisés) : un double-clic ne crée jamais
// deux factures (le serveur refuse aussi une course déjà facturée). Messages d'erreur du serveur affichés tels quels.
// Étape B2 : « Encaissé à bord » sur une course payée par le passager (date et mode de paiement, sans présélection) :
// facture au nom du passager, payée, en une fois (POST /billing/collect-on-board). Téléphones lisibles (lib/phone.js).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, todayIn, PAYMENT_METHODS, paymentLabel } from "@/lib/helpers";
import { formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, CalendarRange, Loader2, AlertTriangle, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Mois précédent (AAAA-MM) d'après le jour de Monaco.
const previousMonth = (tz) => {
  const [y, m] = todayIn(tz).split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
};
const r2 = (x) => Math.round(x * 100) / 100;

export default function ToInvoice({ settings, onCreated }) {
  const cur = settings?.currency || "EUR";
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [busy, setBusy] = useState(false); // une demande en cours : tous les boutons sont grisés
  const busyRef = useRef(false);           // garde immédiate contre le double-clic (avant le nouveau rendu)
  const [mois, setMois] = useState(null);  // payeur dont la fenêtre « Facturer le mois » est ouverte
  const [aBord, setABord] = useState(null); // course dont la fenêtre « Encaissé à bord » est ouverte

  const load = useCallback(() => {
    api.get("/billing/to-invoice").then((r) => { setData(r.data); setErreur(false); }).catch(() => setErreur(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Toute création passe ici : une seule à la fois, message avec le numéro, liste et factures rechargées.
  const creer = async (chemin, corps) => {
    if (busyRef.current) return null;
    busyRef.current = true; setBusy(true);
    try {
      const { data: f } = await api.post(chemin, corps);
      // « Encaissé à bord » : la facture est déjà payée, le message le dit (mode de paiement compris).
      toast.success(f.status === "paid" ? `Facture ${f.number} créée et payée (${paymentLabel(f.payment_method)})`
        : `Facture ${f.number} créée (brouillon)`);
      load(); onCreated?.();
      return f;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
      return null;
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  if (erreur) return <p className="p-6 text-sm text-destructive bg-card border border-border rounded-2xl" data-testid="to-invoice-error">Impossible de charger les courses à facturer : rechargez la page.</p>;
  if (!data) return <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-2xl">Chargement…</p>;
  if (data.payers.length === 0) return <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-2xl" data-testid="to-invoice-empty">Rien à facturer : toutes les courses terminées sont sur une facture.</p>;

  return (
    <div className="space-y-4" data-testid="to-invoice">
      <p className="text-xs text-muted-foreground">Courses terminées (Client absent compris) et frais d'annulation pas encore sur une facture, par payeur ; les plus anciennes en haut.</p>
      {data.payers.map((g) => (
        <div key={g.key} className="bg-card border border-border rounded-2xl shadow-card overflow-hidden" data-testid={`payer-${g.key}`}>
          <div className="p-4 md:p-5 flex flex-wrap items-center gap-3 border-b border-border">
            <div className="min-w-0 flex-1 max-md:basis-full">
              <div className="font-medium break-words" data-testid={`payer-name-${g.key}`}>{g.name}</div>
              <div className="text-xs text-muted-foreground">
                {g.payer === "passenger" ? "Le passager paie" : "Donneur d'ordre"}{g.phone ? ` · ${formatPhone(g.phone)}` : ""} · {g.rides.length} course{g.rides.length > 1 ? "s" : ""}
                {g.overdue_count > 0 && <span className="text-red-600 dark:text-red-400 font-medium" data-testid={`payer-overdue-${g.key}`}> · {g.overdue_count} de plus de {data.overdue_days} jours</span>}
              </div>
            </div>
            <div className="font-display font-bold" data-testid={`payer-total-${g.key}`}>{money(g.total, cur)}</div>
            <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={() => setMois(g)} data-testid={`payer-month-${g.key}`}>
              <CalendarRange className="h-3.5 w-3.5" /> Facturer le mois
            </Button>
          </div>
          <div className="divide-y divide-border">
            {g.rides.map((r) => (
              <div key={r.id} className="px-4 md:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`to-invoice-ride-${r.id}`}>
                <div className="min-w-0 flex-1 max-md:basis-full">
                  <div className="text-sm break-words">{r.label}</div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 text-xs text-muted-foreground">
                    {g.payer === "passenger" && r.orderer_name && <span data-testid={`ride-orderer-${r.id}`}>Donneur d'ordre : {r.orderer_name}</span>}
                    {r.overdue && <span className="px-2 py-0.5 rounded-full font-medium text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300" data-testid={`ride-overdue-${r.id}`}>+ de {data.overdue_days} jours</span>}
                    {r.blocked && <span className="text-amber-700 dark:text-amber-400 inline-flex items-center gap-1" data-testid={`ride-blocked-${r.id}`}><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {r.blocked}</span>}
                  </div>
                </div>
                <div className="font-medium whitespace-nowrap md:ml-auto" data-testid={`ride-amount-${r.id}`}>{money(r.amount, cur)}</div>
                <div className="flex flex-wrap items-center gap-1 max-md:ml-auto">
                  {g.payer === "passenger" && (
                    <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={() => setABord({ ride: r, payer: g })}
                      data-testid={`collect-on-board-${r.id}`}><Wallet className="h-3.5 w-3.5" /> Encaissé à bord</Button>
                  )}
                  <Button size="sm" variant="ghost" className="gap-1.5 rounded-full" disabled={busy}
                    onClick={() => creer("/billing/invoice-ride", { mission_id: r.id })} data-testid={`invoice-ride-${r.id}`}>
                    <FileText className="h-3.5 w-3.5" /> Facturer cette course
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <MonthDialog payer={mois} setPayer={setMois} settings={settings} busy={busy} creer={creer} />
      <OnBoardDialog state={aBord} setState={setABord} settings={settings} busy={busy} creer={creer} />
    </div>
  );
}

// « Facturer le mois » : mois choisi (précédent par défaut), courses du payeur jusqu'à la fin de ce mois (heure de Monaco,
// retardataires comprises, lues sur le serveur), toutes cochées ; décocher en retire une ; total mis à jour.
function MonthDialog({ payer, setPayer, settings, busy, creer }) {
  const cur = settings?.currency || "EUR";
  const [month, setMonth] = useState("");
  const [rides, setRides] = useState(null);
  const [kept, setKept] = useState(new Set());
  const open = !!payer;

  useEffect(() => { if (open) setMonth(previousMonth(settings?.timezone)); }, [open, payer?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !month) return undefined;
    let actif = true;
    setRides(null);
    api.get("/billing/to-invoice", { params: { until: month } })
      .then((r) => {
        if (!actif) return;
        const g = r.data.payers.find((x) => x.key === payer.key);
        const liste = g ? g.rides : [];
        setRides(liste); setKept(new Set(liste.map((x) => x.id)));
      })
      .catch((e) => { if (actif) { setRides([]); toast.error(apiError(e.response?.data?.detail)); } });
    return () => { actif = false; };
  }, [open, month, payer?.key]);

  const total = useMemo(() => r2((rides || []).filter((x) => kept.has(x.id)).reduce((s, x) => s + (x.amount || 0), 0)), [rides, kept]);
  const toggle = (id) => setKept((k) => { const n = new Set(k); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const valider = async () => {
    const ids = rides.filter((x) => kept.has(x.id)).map((x) => x.id);
    const f = await creer("/billing/invoice-month", { payer_key: payer.key, month, mission_ids: ids });
    if (f) setPayer(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) setPayer(null); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="month-dialog">
        <DialogHeader><DialogTitle className="font-display">Facturer le mois — {payer?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Courses jusqu'à la fin du mois</Label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="month-dialog-month" />
            <p className="text-xs text-muted-foreground">Toutes les courses pas encore facturées jusqu'à la fin de ce mois, retardataires comprises. Décochez celles à ne pas mettre sur cette facture.</p>
          </div>
          {rides === null && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {rides && rides.length === 0 && <p className="text-sm text-muted-foreground" data-testid="month-dialog-empty">Aucune course à facturer jusqu'à la fin de ce mois.</p>}
          {rides && rides.length > 0 && (
            <div className="border border-border rounded-xl divide-y divide-border">
              {rides.map((x) => (
                <label key={x.id} className="flex items-start gap-3 p-3 cursor-pointer" data-testid={`month-ride-${x.id}`}>
                  <Checkbox checked={kept.has(x.id)} onCheckedChange={() => toggle(x.id)} className="mt-0.5" data-testid={`month-ride-check-${x.id}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm break-words">{x.label}</span>
                    {x.overdue && <span className="text-xs font-medium text-red-600 dark:text-red-400">+ de 30 jours</span>}
                  </span>
                  <span className={cn("text-sm font-medium whitespace-nowrap", !kept.has(x.id) && "text-muted-foreground line-through")}>{money(x.amount, cur)}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{rides ? `${rides.filter((x) => kept.has(x.id)).length} course(s) cochée(s)` : ""}</span>
            <span className="font-display font-bold text-lg" data-testid="month-dialog-total">{money(total, cur)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => setPayer(null)}>Annuler</Button>
          <Button className="rounded-full gap-2" disabled={busy || !rides || kept.size === 0} onClick={valider} data-testid="month-dialog-create">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Créer la facture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// « Encaissé à bord » : le passager a payé le chauffeur. Date (aujourd'hui à Monaco par défaut) et mode obligatoires ;
// la facture est créée au nom du passager (fiche « Client direct ») et marquée payée, en une seule demande.
function OnBoardDialog({ state, setState, settings, busy, creer }) {
  const cur = settings?.currency || "EUR";
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("");
  const open = !!state;
  useEffect(() => { if (open) { setDate(todayIn(settings?.timezone)); setMethod(""); } }, [open, state?.ride?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const valider = async () => {
    const f = await creer("/billing/collect-on-board", { mission_id: state.ride.id, payment_method: method, paid_at: date });
    if (f) setState(null);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) setState(null); }}>
      <DialogContent className="max-w-sm" data-testid="on-board-dialog">
        <DialogHeader><DialogTitle className="font-display">Encaissé à bord</DialogTitle></DialogHeader>
        {state && (
          <div className="space-y-3">
            <p className="text-sm">
              <span className="font-medium">{state.payer.name}</span> a payé <span className="font-medium">{money(state.ride.amount, cur)}</span> :
              {" "}la facture est créée à son nom et marquée payée.
            </p>
            <p className="text-xs text-muted-foreground break-words">{state.ride.label}</p>
            <div className="space-y-1.5">
              <Label>Date de l'encaissement</Label>
              {/* Jamais après aujourd'hui (jour de Monaco) : le serveur refuse aussi (revue du 2026-11-01). */}
              <Input type="date" value={date} max={todayIn(settings?.timezone)} onChange={(e) => setDate(e.target.value)} data-testid="on-board-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Mode de paiement</Label>
              <Select value={method || undefined} onValueChange={setMethod}>
                <SelectTrigger data-testid="on-board-method"><SelectValue placeholder="Choisir un mode" /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => setState(null)}>Annuler</Button>
          <Button className="rounded-full gap-2" disabled={busy || !method || !date} onClick={valider} data-testid="on-board-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Encaissé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
